import React, { lazy, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { fetchQuestion, fetchRelatedQuestions, formatDate, canPatchQuestion } from '../../utils/asktrip';
import { useLocalStorageState } from '../../utils/useLocalStorageState';
import { localKeyUserTokens, localKeyInstitutionTokens } from '../AskTrip';
import LoadingAnimation from '../../components/LoadingAnimation';

const AskTripQuestionEdit = lazy(() => import('./AskTripQuestionEdit'));

// Page to show a specific question and its answer - https://www.tripdatabase.com/ask/question/:id

export function AskTripQuestion({ questionId }) {

    const [view, _setView] = useState('view'); // 'view' or 'edit'
    const setView = (newView) => {  
        window.scrollTo(0, 0); // scroll to top when changing view
        _setView(newView);
    }

    const [canEdit, setCanEdit] = useState(window.location.hostname === 'localhost');

    let userId = null;
    let institutionId = null;

    // Fetch the question and related questions from the backend
    const [question, setQuestion] = useState(null);

    const [error, setError] = useState(null);

    try {

        const tokens=localStorage.getItem(localKeyUserTokens);
        
        const institutionTokens = localStorage.getItem(localKeyInstitutionTokens);

        if (institutionTokens !== null && institutionTokens !== '') {

            try {
                const parsedInstitutionTokens = JSON.parse(institutionTokens);
                if (parsedInstitutionTokens.institution_auth) {
                    institutionId = parsedInstitutionTokens.institution_auth.id;
                }
            }
            catch (e) {
                // ignore and continue
            }

        }

        let localUserId = null;
        if (tokens !== null || tokens !== '') {


            // attempt to parse the tokens, quietly ignoring any errors
            try {
                const parsedTokens = JSON.parse(JSON.parse(tokens));

                // if we have a tokens object, and and "id" property, use that
                if (parsedTokens.id) {
                    userId= parsedTokens.id;
                }
            }
            catch (e) {
                // ignore and continue
            }
        } 
    }
    catch( e ) {
        // ignore any errors parsing the tokens
    }

    useEffect(() => {

        fetchQuestion( { questionId, userId, institutionId } ).then((data) => {
            setQuestion(data);
        })
            .catch((error) => {
                console.error('Error fetching question:', error);
                setError('Sorry, a problem has occurred while fetching the question.');
            });

        canPatchQuestion()
            .then((canPatch) => {
                setCanEdit(canPatch);  
            })

    }, [questionId]);
    

    if (!question) {
        return <div className="d-flex w-100 justify-content-center align-items-center p-3"><LoadingAnimation /></div>
    }

    if (error) {
        return <div className="alert alert-danger"> {error} </div>
    }

    if (view === 'view') {

        // If we're in view mode, render the view component
        return <AskTripQuestionView question={question} setView={setView} canEdit={canEdit} />;
    }

    if (view === 'edit') {
        // If we're in edit mode, render the edit component
        return (
            <React.Suspense fallback={<LoadingAnimation />}>
                <AskTripQuestionEdit question={question} userId={userId} setView={setView} />
            </React.Suspense>
        );
    }

    return <><code>view={view}?</code></>
}

export function AskTripQuestionView({ question, setView, canEdit }) {

    const questionId = question.questionId;

    const [understoodAlways, setUnderstoodAlways] = useLocalStorageState('asktrip-understood', false);
    const [understood, setUnderstood] = useState(understoodAlways);

    const [related, setRelated] = useState(null);

    useEffect(() => {

        fetchRelatedQuestions(questionId).then((data) => {
                setRelated(data);
        });

        function handleClick(e) {
            if (e.target.tagName === 'A') {
                
                const href = e.target.getAttribute('href');
                if (href.startsWith('#')) {
                    e.preventDefault();
                    const el = document.querySelector(href);
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth' });
                    }
                    window.history.pushState(null, '', href);
                }
                
            }
        }
        
        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
        }

    },[]);

    const [tokens, _setTokens] = useState(localStorage.getItem(localKeyUserTokens));

    const [institutionTokens, _setInstitutionTokens] = useState(localStorage.getItem(localKeyInstitutionTokens));

    // toggle the visibility of the original question text
    const [showOriginal, setShowOriginal] = useState(false);

    // State for showing "copied" feedback
    const [copied, setCopied] = useState(false);

    // State for showing share dropdown on desktop
    const [showShareDropdown, setShowShareDropdown] = useState(false);

    // Check if device is mobile (Bootstrap's md breakpoint is 768px)
    const isMobile = () => window.innerWidth < 768;

    // Handle native sharing
    const handleShare = async () => {
        if (isMobile() && navigator.share && question) {
            // Mobile: use native share
            try {
                await navigator.share({
                    title: question.question,
                    text: `Check out this clinical question on AskTrip: ${question.question}`,
                    url: window.location.href
                });
            } catch (error) {
                // User cancelled or error occurred
                console.log('Share cancelled or failed:', error);
            }
        } else {
            // Desktop: toggle dropdown
            setShowShareDropdown(!showShareDropdown);
        }
    };

    // Handle copy to clipboard
    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setShowShareDropdown(false);
        // Reset after 2 seconds
        setTimeout(() => setCopied(false), 2000);
    };

    // Handle share via email
    const handleShareEmail = () => {
        const subject = encodeURIComponent(`AskTrip: ${question?.question || 'Clinical Question'}`);
        const body = encodeURIComponent(
            `Check out this clinical question on AskTrip:\n\n"${question?.question}"\n\n${window.location.href}`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        setShowShareDropdown(false);
    };

    // Handle share via WhatsApp
    const handleShareWhatsApp = () => {
        const text = encodeURIComponent(
            `Check out this clinical question on AskTrip: "${question?.question}"\n\n${window.location.href}`
        );
        window.open(`https://wa.me/?text=${text}`, '_blank');
        setShowShareDropdown(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showShareDropdown && !event.target.closest('.ask-share-wrapper')) {
                setShowShareDropdown(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showShareDropdown]);

    // Handle report issue email
    const handleReportIssue = () => {
        const subject = encodeURIComponent('AskTrip problematic question');
        const body = encodeURIComponent(
            `Question: "${question?.question || 'n/a'}"\n\n` +
            `Link: ${window.location.href}\n\n` +
            `I think this answer is problematic because...`
        );
        const mailtoLink = `mailto:jon@tripdatabase.com?subject=${subject}&body=${body}`;
        window.location.href = mailtoLink;
    };

    const canSetCookie = () => {

        // don't show the cookie warning if the user is logged in via institution ip (i.e. shared computer)
        const institutionTokens = localStorage.getItem(localKeyInstitutionTokens);
        try {
            const parsedInstitutionTokens = JSON.parse(institutionTokens);
            if (parsedInstitutionTokens.institution_auth) {
                return false;
            }
        }
        catch (e) {
            // ignore JSON parse errors
        }

        return true;

    };

    return <div className="ask-content p-4">
        {!understood &&
            <div className="ask-disclaimer border rounded-3 p-3 bg-white mx-auto d-flex flex-column">
                <div className='row justify-content-md-center'>
                    <h3>Your Responsibility</h3>
                    <div className='col-md-auto col-lg-6'>
                        <p>The information provided by this automated clinical Q&A service is generated using large language models (LLMs), trained on a wide range of biomedical literature and clinical guidelines. While efforts have been made to ensure the accuracy and relevance of responses, users should be aware that LLMs have limitations and may occasionally produce inaccurate or misleading content.</p>
                        <p>This service is intended solely for educational and informational purposes to support the ongoing learning and professional development of qualified healthcare professionals. It does not provide medical advice, diagnosis, or treatment and must not be used as a substitute for clinical judgement.</p>
                    </div>
                    <div className='col-md-auto col-lg-6'>
                        <p>Users are responsible for critically evaluating the information, verifying it against trusted sources, and applying it in the context of each individual patient’s clinical situation. All clinical decisions should be made in consultation with patients and/or their carers, using independent professional judgement.</p>
                        <p>By using this service, you acknowledge these limitations and accept full responsibility for how you interpret and act upon the information provided.</p>
                    </div>
                    </div>
                <button className="btn btn-primary mb-2" onClick={() => setUnderstood(true)}>It's ok, I understand - show the answer</button>
                {canSetCookie && <label>
                    <input type="checkbox" checked={understoodAlways} onChange={(e) => setUnderstoodAlways(e.target.checked)} />
                    &nbsp;Don't warn me of this again
                </label>}
            </div>}
        
        {understood && question &&
            <div className='container'>
                <div className="ask-answer-cols row">
                    <div className="ask-answer-col1 col-md-5 col-lg-4 p-3">
                        <div className='flow'>

                            <h1 className='huge-heading bold'>{question.question}</h1>

                        </div>

                        <div className="d-none d-md-block">

                            { canEdit &&
                                <button className="btn btn-primary mb-2" onClick={() => setView('edit')}>Edit Question</button>
                            }

                            <AskExtrasPartial 
                                copied={copied} 
                                showOriginal={showOriginal} 
                                setShowOriginal={setShowOriginal} 
                                question={question} 
                                handleShare={handleShare} 
                                showShareDropdown={showShareDropdown} 
                                isMobile={isMobile} 
                                handleShareEmail={handleShareEmail} 
                                handleShareWhatsApp={handleShareWhatsApp} 
                                handleCopyToClipboard={handleCopyToClipboard} 
                            />
                        </div>

                        <div className="d-none d-md-block flow">
                            <RelatedQuestionsPartial related={related} />
                        </div>
                    </div>
                    {question.state === 'pending' && <div className="ask-answer-col2 col-md-7 col-lg-8 p-3">This question is still being processed... </div>}
                    {(question.state === 'answered' || question.state==='test') && question.summary &&
                        <div className="ask-answer-col2 col-md-7 col-lg-8 p-3">
                            <div className="ask-clinical-bottom-line p-4 rounded-3 mb-3 bg-white flow">
                                <h3 className='h2 bold text-primary'>Clinical Bottom Line</h3>
                                <p className='h5'>{question.summary.conclusion}</p>
                            </div>
                            <div className="ask-answer p-4 rounded-3 mb-3 bg-white flow">
                                <h3 className='h2 bold text-primary'>Answer</h3>
                                <>{question.summary.answer?.split('\n').map((p, i) => <p key={i}><Markdown>{p}</Markdown></p>)}</>
                            </div>             

                            <div className="ask-references mt-5">
                                <h3 className='paragraph-text bold'>References</h3>
                                <ol className='ps-3 tiny-text'>
                                    {question.summary.references?.map((article, index) => (
                                        <li key={index} id={`ref-${index + 1}`} className="reference">
                                            {article && <><a href={article.url} target="_blank" >{article.title}</a> <br /><i>{article.publication}, {article.date}</i></>}
                                            <br /><br />
                                        </li>
                                    ))}
                                </ol>
                                { question.summary.refCountAI ? <div className="ai-reference d-flex">
                            <span className='tiny-text bold'>A.I.</span>
                            <blockquote className='ps-3 tiny-text'>We use ChatGPT-4o to enhance our RAG-based clinical answers by adding background context, clinical nuance, and filling evidence gaps. ChatGPT-4o is among the most advanced AI models available, consistently achieving over 90% accuracy in benchmark medical Q&A evaluations.</blockquote>
                        </div>: <></>}
                            </div>

                            <div className="ask-actions mb-3">
                                <button className="ask-action-button btn btn-light border px-3 py-2 d-flex" onClick={handleReportIssue}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-triangle-alert-icon lucide-triangle-alert"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg> Report an issue...</button>
                            </div>

                             {/* Mobile versions - shown after the answer */}
                            <div className="d-flex flex-column d-md-none">
                                <AskExtrasPartial  
                                    copied={copied} 
                                    showOriginal={showOriginal} 
                                    setShowOriginal={setShowOriginal} 
                                    question={question} 
                                    handleShare={handleShare} 
                                    showShareDropdown={showShareDropdown} 
                                    isMobile={isMobile} 
                                    handleShareEmail={handleShareEmail} 
                                    handleShareWhatsApp={handleShareWhatsApp} 
                                    handleCopyToClipboard={handleCopyToClipboard} 
                                />
                                <RelatedQuestionsPartial related={related} />
                            </div>

                            <div className='mt-5 flow'>
                                <h5 className='paragraph-text bold'>Disclaimer</h5>
                            
                                <p className='tiny-text'>This clinical Q&A service is intended solely for qualified healthcare professionals and is provided for educational and informational purposes. It does not constitute medical advice, diagnosis, or treatment.</p>

                                <p className='tiny-text'>Our answers are grounded in the best available evidence. Because evidence evolves and can be interpreted differently, the information supplied may occasionally diverge from common practice and, in rare instances, from local or institutional guidelines. Users must therefore verify that any recommendations align with current standards in their own setting.</p>

                                <p className='tiny-text'>The developers make no guarantees regarding the accuracy, completeness, or applicability of the content generated. Use of this service is entirely at the user’s discretion and risk, and users must apply their own independent clinical judgment and consult authoritative sources before making clinical decisions.</p>

                                <p className='tiny-text'>The developers accept no liability for any loss, harm, or damage resulting from decisions made or actions taken on the basis of information provided by this service.</p>

                        </div>

                        </div>}
                </div>
                
               

            </div>}
    </div>


}

function convertMarkdownToHtml(text) {

    // Replace ### with <h4> tags
    text = text.replace(/^###\s*(.+)$/gm, "<h4>$1</h4>");

    // Replace ## with <h3> tags
    text = text.replace(/^##\s*(.+)$/gm, "<h3>$1</h3>");

    // Replace ** with <b> tags
    text = text.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");

    // Replace \n with <br> tags
    text = text.replace(/\n/g, "<br>");

    return text;
}

function Markdown({ children }) {
    const html = convertMarkdownToHtml(children);
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function AskExtrasPartial({ copied, showOriginal, setShowOriginal, question, handleShare, showShareDropdown, isMobile, handleShareEmail, handleShareWhatsApp, handleCopyToClipboard }) {

    const [showQualityModal, setShowQualityModal] = useState(false);

    return (
        <div className="ask-extras d-flex flex-column align-items-start">

            <div className="ask-answer-meta pt-3 pb-5">
                            
                <div className="ask-areas d-flex">
                    <h4 className="fs-6 text-muted w-50 mb-1 pe-2 ">Clinical area</h4>
                    <ul className="list-unstyled w-50 d-inline fw-bold m-0 p-0">
                        {question.areas.map((a, i) => (
                            <li key={a} className="d-inline">{a}{i < question.areas.length - 1 && ', '}</li>
                        ))}
                    </ul>
                </div>

                <div className="ask-types d-flex">
                    <h4 className="fs-6 text-muted w-50 mb-1 pe-2">Question type</h4>
                    <ul className="list-unstyled w-50 d-inline fw-bold m-0 p-0">
                        {question.types.map((a, i) => (
                            <li key={a} className="d-inline">{a}{i < question.types.length - 1 && ', '}</li>
                        ))}
                    </ul>
                </div>

                <div className="ask-quality d-flex">
                    <QualityModal
                        show={showQualityModal}
                        onHide={() => setShowQualityModal(false)}
                    />
                    <h4 className="fs-6 text-muted w-50 mb-1 pe-2">Quality of evidence <button
                        type="button"
                        className="btn dropdown-toggle-split button-mindmap-toggle"
                        style={{ marginLeft: "1px" }}
                        onClick={()=> setShowQualityModal(true)}
                        >
                        ?<span className="visually-hidden">Evidence Quality Explained</span>
                        </button>
                    </h4>
                    <ul className="list-unstyled w-50 d-inline fw-bold m-0 p-0">
                        <li className="d-inline">{question.quality || 'n/a'}</li>
                    </ul>
                </div>

                <div className="ask-date d-flex">
                    <h4 className="fs-6 text-muted w-50 mb-1 pe-2">Question asked</h4>
                    <ul className="list-unstyled w-50 d-inline fw-bold m-0 p-0">
                        <li className="d-inline">{formatDate(question.timestamp)}</li>
                    </ul>
                </div>

            </div>

            <div className="ask-share-wrapper position-relative mb-3">

                <button className="ask-action-button btn btn-light border px-3 py-2 d-flex align-items-center" onClick={handleShare}>
                    {copied ? (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="me-2">
                                <path d="M20 6L9 17L4 12" stroke="green" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Copied to clipboard!
                        </>
                    ) : (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="me-2">
                                <path d="M15 17L20 12M20 12L15 7M20 12H8C6.93913 12 5.92172 12.4214 5.17157 13.1716C4.42143 13.9217 4 14.9391 4 16V18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Share This Page
                        </>
                    )}
                </button>
                {showShareDropdown && !isMobile() && (
                    <div className="dropdown-menu show position-absolute" style={{ top: '100%', left: 0, marginTop: '5px' }}>
                        <button className="dropdown-item d-flex align-items-center" onClick={handleShareEmail}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                                <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                                <path d="m22 7-10 5L2 7"></path>
                            </svg>
                            Email
                        </button>
                        <button className="dropdown-item d-flex align-items-center" onClick={handleShareWhatsApp}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                            </svg>
                            WhatsApp
                        </button>
                        <button className="dropdown-item d-flex align-items-center" onClick={handleCopyToClipboard}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                            Copy link
                        </button>
                    </div>
                )}
            </div>
            <a className="ask-action-button btn btn-light border px-3 py-2 d-flex mb-3" href="/ask"><svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                <path d="M9.59009 9.00008C9.82519 8.33175 10.2892 7.76819 10.9 7.40921C11.5108 7.05024 12.229 6.91902 12.9273 7.03879C13.6255 7.15857 14.2589 7.52161 14.7152 8.06361C15.1714 8.60561 15.4211 9.2916 15.4201 10.0001C15.4201 12.0001 12.4201 13.0001 12.4201 13.0001M12.5 17H12.51M8.4 20C10.3086 20.9791 12.5041 21.2443 14.5909 20.7478C16.6777 20.2514 18.5186 19.0259 19.7818 17.2922C21.045 15.5586 21.6474 13.4308 21.4806 11.2922C21.3137 9.15366 20.3886 7.14502 18.8718 5.62824C17.355 4.11146 15.3464 3.1863 13.2078 3.01946C11.0693 2.85263 8.94147 3.45509 7.20782 4.71829C5.47417 5.98149 4.24869 7.82236 3.75222 9.90916C3.25575 11.996 3.52094 14.1915 4.5 16.1L2.5 22L8.4 20Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg> Ask a New Question</a>
            <a className="ask-original-toggle text-dark tiny-text" onClick={() => setShowOriginal(!showOriginal)}>Show original question</a>
            {showOriginal && <h2 className="ask-original-question">&ldquo;{question.original}&rdquo;</h2>}

            

        </div>
    );
}

function RelatedQuestionsPartial({ related }) {
    if (!related || related.length===0) return null;
    
    return (
        <div className="ask-related-questions pt-5">
            <h4 className='text-muted paragraph-text'>Related Questions</h4>
            <ul className="list-unstyled me-5">
                {related.map((q, i) => (
                    <li key={q.questionId}>
                        <a href={`/ask/${q.slug}-${q.questionId}`} className="text-primary">{q.question}</a> 
                    </li>
                ))}
            </ul>
        </div>
    );
}


const QualityModal = ({ show, onHide }) => {
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Quality of evidence</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          This reflects the quality and consistency of the sources behind the answer – not whether the answer itself is definitive. A high score indicates strong underlying evidence, even if that evidence highlights uncertainty or conflicting findings. <a target="_blank" href="https://blog.tripdatabase.com/2025/06/19/understanding-the-strength-of-evidence-in-our-clinical-qa-system/">See more</a>
        </p>
      </Modal.Body>
    </Modal>
  );
};